import React, { memo, useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import logoImage from '@assets/build-w-ferguson.png';

const Logo = memo(({ height = 50, width = 'auto', priority = false, ...props }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Memoized styles to prevent recalculation
  const containerStyles = useMemo(() => ({
    height,
    width,
    display: 'flex',
    alignItems: 'center',
    position: 'relative'
  }), [height, width]);

  const imageStyles = useMemo(() => ({
    height: '100%',
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
    transition: 'opacity 0.3s ease',
    opacity: imageLoaded ? 1 : 0
  }), [imageLoaded]);

  // Optimized event handlers
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  // Fallback component for errors
  const ErrorFallback = memo(() => (
    <Box
      sx={{
        height: '100%',
        width: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        color: 'grey.600',
        fontSize: '0.75rem',
        px: 1,
        borderRadius: 1
      }}
    >
      Ferguson
    </Box>
  ));

  return (
    <Box sx={containerStyles} {...props}>
      {/* Loading skeleton */}
      {!imageLoaded && !imageError && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 1
          }}
        />
      )}

      {/* Main logo image */}
      {!imageError && (
        <img
          src={logoImage}
          alt="Build with Ferguson"
          style={imageStyles}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}

      {/* Error fallback */}
      {imageError && <ErrorFallback />}
    </Box>
  );
});

Logo.displayName = 'Logo';

export { Logo };
export default Logo;

// Change import to use craco alias for assets if you have one, otherwise keep as is.
// If you want to rename this file to Logo.js for consistency, do so and update imports elsewhere.
// Otherwise, no changes are needed for the alias issue here, as this file does not import from @utils.
